import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from "react";

/**
 * State and handlers for {@link ConfirmationDialog}: snapshot content while open, async confirm
 * with loading UI, and guarded dismiss while confirm is in flight.
 *
 * @param {object} params
 * @param {boolean} params.open Controlled dialog open state.
 * @param {(open: boolean) => void} params.onOpenChange Parent Radix open callback.
 * @param {string} params.title Dialog heading.
 * @param {string} params.body Dialog body copy.
 * @param {string} params.confirmLabel Primary button label when idle.
 * @param {string | undefined} params.confirmPendingLabel Primary button label while async confirm runs.
 * @param {(() => void) | (() => Promise<unknown>) | undefined} params.onConfirm Primary action.
 * @param {string | undefined} params.contentClassName Extra class names on dialog content.
 * @returns {{
 *   isConfirmLoading: boolean,
 *   visibleContent: { title: string, body: string, confirmLabel: string, confirmPendingLabel: string | undefined, onConfirm: typeof params.onConfirm },
 *   pendingLabel: string,
 *   contentClasses: string,
 *   handleRootOpenChange: (nextOpen: boolean) => void,
 *   handleConfirmPrimary: () => void,
 *   dismiss: () => void,
 *   blockDismissWhileLoading: (event: { preventDefault: () => void }) => void,
 * }}
 */
export function useConfirmation({
    open,
    onOpenChange,
    title,
    body,
    confirmLabel,
    confirmPendingLabel,
    onConfirm,
    contentClassName,
}) {
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);
    const [visibleContent, setVisibleContent] = useState({
        title,
        body,
        confirmLabel,
        confirmPendingLabel,
        onConfirm,
    });

    useEffect(() => {
        if (!open) {
            setIsConfirmLoading(false);
        }
    }, [open]);

    useLayoutEffect(() => {
        if (open) {
            setVisibleContent({
                title,
                body,
                confirmLabel,
                confirmPendingLabel,
                onConfirm,
            });
        }
    }, [body, confirmLabel, confirmPendingLabel, onConfirm, open, title]);

    const handleRootOpenChange = useCallback(
        (nextOpen) => {
            if (!nextOpen && isConfirmLoading) {
                return;
            }
            onOpenChange(nextOpen);
        },
        [isConfirmLoading, onOpenChange],
    );

    const dismiss = useCallback(() => {
        onOpenChange(false);
    }, [onOpenChange]);

    const handleConfirm = useCallback(async () => {
        const fn = visibleContent.onConfirm ?? onConfirm;
        if (!fn) {
            onOpenChange(false);
            return;
        }

        const result = fn();
        if (result != null && typeof result.then === "function") {
            setIsConfirmLoading(true);

            try {
                await result;
            } catch {
                /* errors handled by caller (e.g. mutation onError) */
            } finally {
                setIsConfirmLoading(false);
            }
        }
        
        onOpenChange(false);
    }, [onConfirm, onOpenChange, visibleContent.onConfirm]);

    const handleConfirmPrimary = useCallback(() => {
        void handleConfirm();
    }, [handleConfirm]);

    const blockDismissWhileLoading = useCallback(
        (event) => {
            if (isConfirmLoading) {
                event.preventDefault();
            }
        },
        [isConfirmLoading],
    );

    const contentClasses = useMemo(
        () =>
            ["confirmation-dialog__content", contentClassName]
                .filter(Boolean)
                .join(" "),
        [contentClassName],
    );

    const pendingLabel = useMemo(
        () => visibleContent.confirmPendingLabel ?? visibleContent.confirmLabel,
        [visibleContent.confirmLabel, visibleContent.confirmPendingLabel],
    );

    return {
        isConfirmLoading,
        visibleContent,
        pendingLabel,
        contentClasses,
        handleRootOpenChange,
        handleConfirmPrimary,
        dismiss,
        blockDismissWhileLoading,
    };
}
